import { toast } from "react-toastify";

const tradeFlowErrorsLib = {
  quoteOutright: {
    noBidquoteSideER: "Missing bid",
    noAskquoteSideER: "Missing ask",
    missingSpreadTwoWay: "Missing quote values",
  },
  quoteStructure: {
    oneBlockWasQuoted:
      "Missing quotes. Quote spread level, or quote all blocks",
    noBlocksQuoted: "Missing quotes. Quote spread level, or quote all blocks",
    missingSpreadTwoWay: "Missing quote values on spread level",
  },
  quoteSynRepo: {
    noBidquoteSideER: "Missing repo bid",
    noAskquoteSideER: "Missing repo ask",
    missingSpreadTwoWay: "Missing quote values on spread level",
  },
  //
  //
  executeSynRepo: {
    noBidquoteSideER: "Missing repo bid",
    noAskquoteSideER: "Missing repo ask",
    allBlocksNotQuoted: "All blocks need to be quoted",
  },
  executeStructure: {
    moreThan1BlocksNotQuoted:
      "Only 1 block can be left unquoted for this structure",
    allBlocksNotQuoted: "Missing block values",
  },
  affirmTrade: {
    allBlocksNotQuoted:
      "Missing block values",
  },
  dollarFRQuote: {
    incorrectValue:
      "Invalid quote format, enter a number or fraction",
  },
  other: {
    incorrectTimeFormat: "Invalid time format",
  },
  // LQC-1040: iceberg release size can only be increased once trade is sent
  icebergSize: {
    attemptedReduction: "Iceberg size can only be increased, not reduced",
  },
};

type Action = keyof typeof tradeFlowErrorsLib; // Defines Action as a union of all keys in tradeFlowErrorsLib
type Outcome<A extends Action> = keyof (typeof tradeFlowErrorsLib)[A]; // Defines Outcome as the keys within the selected tradeFlowErrorsLib[action] sub-object, depending on the provided action


export function generateToastMessageForTradeFlowErrors<A extends Action>({
  action,
  outcome,
}: {
  action: A;
  outcome: Outcome<A>;
}) {
  // Use error message if valid outcome, else default error
  const errorMessage =
    tradeFlowErrorsLib[action][outcome] ?? "An error has occurred";

  // Cast errorMessage to a string to satisfy the toast.error function
  toast.error(errorMessage as string);
}

interface IESIDAndAdditionalData {
  ESIDPassed: string | undefined;
  additionalData: any;
}

export function generateNotDealtToastTradeCreation(status:string) {
    if(status == "Not Dealt")
      toast.success("Block set to 'Not Dealt'");
    else if (status == "Unassigned")
      toast.success("Block Re-opened");
}

export function generateMissingQuotesForQuotedSizeNotification() {
  toast.warn("Changes in quoted size require block quotes")
}
const toastWithMessageLib = {
  sendToPannelStoreAction: {
    error: "No counterparties selected",
    success: ({ additionalData }: IESIDAndAdditionalData) =>
      `Sending to: ${additionalData}`, // data is a list of the CP's the trade has gone to.
  },
  editPanelStoreAction: {
    error: "Error editing panel",
    success: () => `Panel Updated`, // data is a list of the CP's the trade has gone to.
  },

  reSendStoreAction: {
    error: null, // empty string means error message is not getting called.
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} resent`,
  },
  awardStoreAction: {
    error:
      "No best counterparty. Right click on a counterparty with quotes to award",
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} awarded`,
  },
  notDealtStoreAction: {
    error: null, // empty string means error message is not getting called.
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} set to 'Not Dealt'`,
  },
  reOpenStoreAction: {
    error: null, // empty string means error message is not getting called.
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} reopened`,
  },
  acknowledgeStoreAction: {
    error: null, // empty string means error message is not getting called.
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} acknowledged`,
  },
  // executeTrade: { // currently no meage for execute.
  //   error: null,
  //   success: ({ ESIDPassed, additionalData }: {ESIDPassed: string | undefined, additionalData: any}) => null,
  // },
  unawardStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} unawarded`,
  },
  recallStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} recalled`,
  },
  rejectStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} rejected`,
  },
  affirmStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} affirmed`,
  },
  moveToAwardedOPStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} moved to 'Awarded'`,
  },
  offQuoteStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} set to 'Off-Quote'`,
  },
  reQuoteStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `Requesting requote for ${ESIDPassed}.`,
  },
  moveStatusBackOPStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} status moved back`,
  },
  pullRequestStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} request pulled`,
  },
  agreeMoveToAwardedRPStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} agreed to move to 'Awarded'`,
  },
  moveStatusBackRPStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} status requested to be moved back`,
  },
  acceptLevelStoreAction: {
    error: null,
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} level accepted`,
  },

  // QUOTING:
  quoteTradeStoreAction: {
    error: "Error sending quote",
    success: ({
      ESIDPassed,
      additionalData,
    }: {
      ESIDPassed: string | undefined;
      additionalData: any;
    }) => {
      if (additionalData) {
        return `${ESIDPassed} quoted. ${additionalData}.`;
      } else return `${ESIDPassed} quoted`;
    },
  },

  executeTradeStoreAction: {
    error: "Error executing",
    success: ({ ESIDPassed }: IESIDAndAdditionalData) =>
      `${ESIDPassed} executed`,
  },
  setToNew: {
    error: "Error setting trade to 'New'",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} Set back to 'New'`,
  },
  updateSettlement: {
    error: "Error updating settlement",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} settlement date updated`,
  },
  updateDelivery: {
    error: "Error updating termination date",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} termination date updated`,
  },
  updateSecurity: {
    error: "Error updating Security",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} Security updated`,
  },
  // LQC-590: Price notation ($p/$fr) cannot be quoted at 0 - checked first
  priceQuotedWith0: {
    error: "Price cannot be quoted at 0",
    success: (_args: { ESIDPassed: string | undefined }) => ``,
  },
  // LQC-889: Price notation ($p/$fr) cannot be quoted negative
  priceQuotedNegative: {
    error: "Price cannot be negative",
    success: (_args: { ESIDPassed: string | undefined }) => ``,
  },
  // LQC-896: Set Level on Orders cannot be negative for price notation ($p/$fr)
  orderLevelNegative: {
    error: "Order Level cannot be negative",
    success: (_args: { ESIDPassed: string | undefined }) => ``,
  },
  // FUT Quote = 0
  FUTQuotedWith0: {
    error: "FUT cannot be quoted at 0",
    success: (_args: { ESIDPassed: string | undefined }) => ``,
  }, // ONLY used for an error message.

  // Fill price validation (mirrors quote-side rules for Orders)
  fillPriceAtZero: {
    error: "Fill price cannot be 0",
    success: (_args: { ESIDPassed: string | undefined }) => ``,
  },
  fillPriceNegative: {
    error: "Fill price cannot be negative",
    success: (_args: { ESIDPassed: string | undefined }) => ``,
  },

  noFeesOnRecoupon: {
    error: "Missing block unwind amount",
    success: (_args: { ESIDPassed: string | undefined }) => ``,
  },

  //Book selection
  updateBook: {
    error: "Error updating book",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} Book updated.`,
  },

  // Cancelling a trade:
  cancelInOMSStoreAction: {
    error: "Error cancelling in OMS",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} cancel sent to OMS`,
  },

  recreateBlocksStoreAction: {
    error: "Error attempting to re-create blocks",
    success: () => `Blocks actioned for re-creation`,
  },

  offloadRiskStoreAction: {
    error: "Error attempting to off-load risk",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} blocks created to off-load risk`,
  },

  // ---- ORDERS ----- //
  // ----------------- //
  workingStoreAction: {
    error: "Error attempting to work order",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} working order`,
  },
  editFilledStoreAction: {
    error: "Error requesting fill edit",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} set to editing fills`,
  },
  updateFillsStoreAction: {
    error: "Error updating fills",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} fills updated`,
  },
  noMoreFillsStoreAction: {
    error: "Error declaring 'No More Fills'",
    success: ({ ESIDPassed }: { ESIDPassed: string | undefined }) =>
      `${ESIDPassed} updated to confirm no more fills.`,
  },
  noFillsOnAnyBlock: {
    error: "At least one fill is required",
    success: () => ``,
  },
};

export function generateToastMessageTradeData({
  action,
  result,
  ESIDPassed,
  additionalData,
}: {
  action: keyof typeof toastWithMessageLib; // restricts to keys of toastWithMessageLib;
  result: "error" | "success" | "missingTransactionFee";
  ESIDPassed?: string;
  additionalData?: any;
}) {
  // Allow the button to be clicked again.
  // The setStateAction will only be passed in on succuess actions unless at new.
  // Some actions will not pass the function in as they can allow many actions in a row - such as updating dates.

  if (result == "error") {
    toast.error(
      toastWithMessageLib[action]?.[result] ?? "An error has occurred",
    );
  } else if (result == "success") {
    toast.success(
      toastWithMessageLib[action]?.[result]({ ESIDPassed, additionalData }) ??
        "Action successful.",
    );
  } else if (result == "missingTransactionFee") {
    toast.error(
      "Executed counterparty is missing transaction fee(s). Could not Affirm trade.",
    );
  }
}

const toastsForSendPanels = {
  addPanel: {
    error: (additionalData?: string) =>
      `Error adding another 'new panel'. ${additionalData}`,
    success: () => `'New panel' added to drop down list`,
  },
  savePanel: {
    error: () => "Cannot save over panel default.",
    success: (additionalData?: string) =>
      `Selected counterparties saved to ${additionalData}`,
  },
  editNamePanel: {
    error: () => "Error adding another 'new panel'",
    success: () => "'New panel' added to drop down list",
  },
};

export function generateToastMessageSelectingPanel({
  action,
  result,
  additionalData,
}: {
  action: keyof typeof toastsForSendPanels;
  result: "error" | "success";
  additionalData?: string;
}) {
  // Use error message if valid outcome, else default error
  if (result == "error") {
    toast.error(
      toastsForSendPanels[action]?.[result](additionalData) ??
        "An error has occurred",
    );
  } else if (result == "success") {
    toast.success(
      toastsForSendPanels[action]?.[result](additionalData) ??
        "Action successful",
    );
  }
}
